"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UnsupportedPlatformCapabilityError = exports.LAUNCHABLE_PLATFORM_NAMES = exports.PLATFORM_CAPABILITY_REGISTRY = exports.PLATFORM_CAPABILITIES = exports.PLATFORM_CAPABILITY_KEYS = exports.PLATFORM_NAMES = void 0;
exports.isPlatformName = isPlatformName;
exports.isLaunchablePlatformName = isLaunchablePlatformName;
exports.getLaunchablePlatformsSupporting = getLaunchablePlatformsSupporting;
exports.getPlatformCapabilities = getPlatformCapabilities;
exports.getPlatformCapability = getPlatformCapability;
exports.isPlatformCapabilitySupported = isPlatformCapabilitySupported;
exports.platformSupportsCapability = platformSupportsCapability;
exports.getPlatformsSupportingCapability = getPlatformsSupportingCapability;
exports.requirePlatformCapability = requirePlatformCapability;
/**
 * Platforms with active product execution paths. Keep this in lockstep with
 * active entries in `PLATFORM_CAPABILITY_REGISTRY`; the backend parity gate
 * enforces that relationship.
 */
exports.PLATFORM_NAMES = [
    "META",
    "TIKTOK",
    "GOOGLE",
    "SNAPCHAT",
    "APPLOVIN",
];
const PLATFORM_NAME_SET = new Set(exports.PLATFORM_NAMES);
function isPlatformName(value) {
    return PLATFORM_NAME_SET.has(value);
}
/**
 * Product surfaces whose availability varies by ad platform.
 *
 * Keep this list at the level where callers need to route or hide behavior.
 * Platform-specific variants within a supported surface belong in `notes`,
 * not in ad-hoc platform unions at the call site.
 */
exports.PLATFORM_CAPABILITY_KEYS = [
    "connection",
    "adSync",
    "systemAHarvest",
    "query",
    "enrichment",
    "frameworkLaunch",
    "frameworkMonitoring",
    "frameworkPausedAdReconciliation",
    "frameworkReporting",
    "frameworkCraftFindings",
    "frameworkRemonitoring",
    "scaleGraduation",
    "scaleCampaignCreation",
    "sandbox",
    "systemB",
    "uiAdsBrowser",
    "uiFramework",
    "uiMetrics",
    "uiSharedSurfaces",
];
const supported = (notes) => notes ? { status: "supported", notes } : { status: "supported" };
const unsupported = (reason, disposition = "not-implemented") => ({
    status: "unsupported",
    disposition,
    reason,
});
/**
 * Canonical platform capability declarations.
 *
 * The `satisfies Record<PlatformName, PlatformCapabilities>` constraint
 * requires every active `PlatformName` to declare every capability. Backend
 * parity tests separately keep those active names synchronized with the
 * lifecycle registry and Prisma enum. Values reflect implemented routing, not
 * intended roadmap state.
 * Make lifecycle and candidate changes in `PLATFORM_CAPABILITY_REGISTRY`.
 */
exports.PLATFORM_CAPABILITIES = {
    META: {
        connection: supported(),
        adSync: supported(),
        systemAHarvest: supported(),
        query: supported(),
        enrichment: supported(),
        frameworkLaunch: supported("v1 and v2"),
        frameworkMonitoring: supported(),
        frameworkPausedAdReconciliation: supported(),
        frameworkReporting: supported(),
        frameworkCraftFindings: supported(),
        frameworkRemonitoring: supported(),
        scaleGraduation: supported("Includes social proof, multi-aspect creatives, and settings differences"),
        scaleCampaignCreation: supported(),
        sandbox: supported(),
        systemB: supported(),
        uiAdsBrowser: supported(),
        uiFramework: supported(),
        uiMetrics: supported(),
        uiSharedSurfaces: supported(),
    },
    TIKTOK: {
        connection: supported(),
        adSync: supported(),
        systemAHarvest: unsupported("System A harvesting is intentionally deferred for TikTok", "deferred"),
        query: supported(),
        enrichment: supported(),
        frameworkLaunch: supported("v1, v2, and Smart+"),
        frameworkMonitoring: supported(),
        frameworkPausedAdReconciliation: supported(),
        frameworkReporting: supported(),
        frameworkCraftFindings: supported(),
        frameworkRemonitoring: supported(),
        scaleGraduation: supported("Classic and Smart+"),
        scaleCampaignCreation: unsupported("Scale campaigns are import-only for TikTok", "deferred"),
        sandbox: supported(),
        systemB: supported(),
        uiAdsBrowser: supported(),
        uiFramework: supported(),
        uiMetrics: supported(),
        uiSharedSurfaces: supported(),
    },
    GOOGLE: {
        connection: supported(),
        adSync: supported("Demand Gen and video-family ad types only; YouTube-hosted videos store thumbnail-only media"),
        systemAHarvest: supported(),
        query: supported(),
        enrichment: unsupported("Google query rows are not enriched with ad names or previews"),
        frameworkLaunch: supported("v2 only"),
        frameworkMonitoring: supported(),
        frameworkPausedAdReconciliation: supported(),
        frameworkReporting: supported(),
        frameworkCraftFindings: supported(),
        frameworkRemonitoring: supported(),
        scaleGraduation: supported("Winners graduate into existing scale campaigns via GoogleScalePlatformAdapter (Demand Gen + video; same-account asset reuse with GCS re-upload fallback)"),
        scaleCampaignCreation: unsupported("Google scale campaigns are import-only; no in-UI campaign creation yet"),
        sandbox: supported("Sandbox accounts + framework routing; demo batches are seeded Meta-only"),
        systemB: unsupported("System-B Sheets reporting does not include Google", "deferred"),
        uiAdsBrowser: supported("Ad sync covers Demand Gen + video-family ads; YouTube-hosted videos preview as thumbnails only"),
        uiFramework: supported(),
        uiMetrics: supported(),
        uiSharedSurfaces: supported("Performance-dashboard channel filter. Spend, clicks, and impressions only — Google contributes no hook/hold-rate columns, and rows are unenriched (no ad names or previews)"),
    },
    SNAPCHAT: {
        connection: supported(),
        adSync: supported(),
        systemAHarvest: supported("Lifetime per-ad totals via the account-level stats endpoint (no ad-id filter — one request per account, two-year retention window)"),
        query: supported(),
        enrichment: supported(),
        frameworkLaunch: supported("v2 only"),
        frameworkMonitoring: supported(),
        frameworkPausedAdReconciliation: supported(),
        frameworkReporting: supported(),
        frameworkCraftFindings: supported(),
        frameworkRemonitoring: supported(),
        scaleGraduation: supported("Winners graduate into existing scale campaigns via SnapchatScalePlatformAdapter (creative reuse; same-account)"),
        scaleCampaignCreation: unsupported("Snapchat scale campaigns are import-only; no in-UI campaign creation yet"),
        sandbox: supported("Sandbox accounts + framework routing; demo batches are seeded Meta-only"),
        systemB: unsupported("System-B Sheets reporting does not include Snapchat", "deferred"),
        uiAdsBrowser: supported(),
        uiFramework: supported(),
        uiMetrics: supported(),
        uiSharedSurfaces: unsupported("Snapchat is still omitted from several shared product surfaces"),
    },
    APPLOVIN: {
        connection: supported("Report key entered by hand and stored encrypted; there is no OAuth flow and no endpoint that lists a user's accounts"),
        adSync: unsupported("AppLovin is reporting-only: the dashboard reads the Reporting API live, and no creative assets are ingested"),
        systemAHarvest: unsupported("System A harvesting has no AppLovin path", "deferred"),
        query: supported("Advertiser Reporting API. Spend, impressions, clicks, conversions and sales; 88-day retention window, UTC, day and hour grain"),
        enrichment: unsupported("AppLovin rows carry the creative's name from the report itself, but there are no previews and no ad ids to join on"),
        frameworkLaunch: unsupported("Framework does not launch on AppLovin"),
        frameworkMonitoring: unsupported("Framework does not monitor AppLovin"),
        frameworkPausedAdReconciliation: unsupported("No Framework batches run on AppLovin"),
        frameworkReporting: unsupported("No Framework batches run on AppLovin"),
        frameworkCraftFindings: unsupported("No Framework batches run on AppLovin"),
        frameworkRemonitoring: unsupported("No Framework batches run on AppLovin"),
        scaleGraduation: unsupported("Scale campaigns do not include AppLovin"),
        scaleCampaignCreation: unsupported("Scale campaigns do not include AppLovin"),
        sandbox: unsupported("No sandbox or demo AppLovin accounts"),
        systemB: unsupported("System-B Sheets reporting does not include AppLovin", "deferred"),
        uiAdsBrowser: unsupported("Ads Browser lists synced creatives, and AppLovin creatives are not synced"),
        uiFramework: unsupported("Framework surfaces do not offer AppLovin"),
        uiMetrics: supported("Custom-metric and conversion-funnel setup. Spend, installs, purchases, first purchases and target events; SIMPLE_LOOKUP columns only, since the advertiser report has no per-action breakdown"),
        uiSharedSurfaces: supported("Performance-dashboard channel filter. Spend, impressions, clicks, conversions and sales; rows are unenriched, and a request older than 88 days is clamped with a warning"),
    },
};
/**
 * Canonical lifecycle and capability registry for Prisma ad platforms.
 *
 * Adding a Prisma enum member starts here as a `candidate` with an explicit
 * tracking issue and reason. Promotion requires changing the discriminant to
 * `active`, recording the activation decision, and supplying the complete
 * capability matrix. The backend parity gate then requires all structural and
 * executable evidence for the promoted platform.
 *
 * Known `PlatformName` keys are compile-time active here. Completeness against
 * Prisma's enum is deliberately enforced in the backend parity test/gate,
 * because this shared package does not depend on generated Prisma artifacts.
 */
exports.PLATFORM_CAPABILITY_REGISTRY = {
    META: {
        status: "active",
        activation: {
            trackingIssue: "NEW-582",
            summary: "Existing Meta production platform registered for parity gates",
        },
        capabilities: exports.PLATFORM_CAPABILITIES.META,
    },
    TIKTOK: {
        status: "active",
        activation: {
            trackingIssue: "NEW-582",
            summary: "Existing TikTok production platform registered for parity gates",
        },
        capabilities: exports.PLATFORM_CAPABILITIES.TIKTOK,
    },
    GOOGLE: {
        status: "active",
        activation: {
            trackingIssue: "NEW-582",
            summary: "Existing Google production platform registered for parity gates",
        },
        capabilities: exports.PLATFORM_CAPABILITIES.GOOGLE,
    },
    SNAPCHAT: {
        status: "active",
        activation: {
            trackingIssue: "NEW-582",
            summary: "Existing Snapchat production platform registered for parity gates",
        },
        capabilities: exports.PLATFORM_CAPABILITIES.SNAPCHAT,
    },
    APPLOVIN: {
        status: "active",
        activation: {
            trackingIssue: "NEW-593",
            summary: "Read-only reporting platform: performance-dashboard channel backed by the advertiser Reporting API, with sync, Framework, and scale explicitly out of scope",
        },
        capabilities: exports.PLATFORM_CAPABILITIES.APPLOVIN,
    },
};
exports.LAUNCHABLE_PLATFORM_NAMES = exports.PLATFORM_NAMES.filter((platform) => isPlatformCapabilitySupported(exports.PLATFORM_CAPABILITIES[platform].frameworkLaunch));
function isLaunchablePlatformName(value) {
    return exports.LAUNCHABLE_PLATFORM_NAMES.includes(value);
}
/**
 * Launchable platforms supporting a capability. The launch, monitoring, and
 * reporting subsystems are typed to the launchable set, so they need the
 * narrower return type rather than filtering `PlatformName[]` at every caller.
 */
function getLaunchablePlatformsSupporting(capability) {
    return exports.LAUNCHABLE_PLATFORM_NAMES.filter((platform) => exports.PLATFORM_CAPABILITIES[platform][capability].status === "supported");
}
function getPlatformCapabilities(platform) {
    return exports.PLATFORM_CAPABILITIES[platform];
}
function getPlatformCapability(platform, capability) {
    return exports.PLATFORM_CAPABILITIES[platform][capability];
}
function isPlatformCapabilitySupported(support) {
    return support.status === "supported";
}
function platformSupportsCapability(platform, capability) {
    return isPlatformCapabilitySupported(getPlatformCapability(platform, capability));
}
function getPlatformsSupportingCapability(capability) {
    return exports.PLATFORM_NAMES.filter((platform) => exports.PLATFORM_CAPABILITIES[platform][capability].status === "supported");
}
class UnsupportedPlatformCapabilityError extends Error {
    constructor(platform, capability, support) {
        super(`${platform} does not support ${capability}: ${support.reason}`);
        this.platform = platform;
        this.capability = capability;
        this.support = support;
        this.code = "UNSUPPORTED_PLATFORM_CAPABILITY";
        this.name = "UnsupportedPlatformCapabilityError";
    }
}
exports.UnsupportedPlatformCapabilityError = UnsupportedPlatformCapabilityError;
function requirePlatformCapability(platform, capability) {
    const support = getPlatformCapability(platform, capability);
    if (!isPlatformCapabilitySupported(support)) {
        throw new UnsupportedPlatformCapabilityError(platform, capability, support);
    }
    return support;
}
